import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';

import { cleanEntity } from '../shared/util/entity-utils/entity-utils';
import { createEntitySlice, EntityState, getAllRequest, serializeAxiosError } from '../shared/reducers/reducer.utils';

const initialState: EntityState<any> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: {},
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const GRUPO_EMPRESARIAL_API = 'api/grupo-empresarials';

export function contactosCollectionUrl(grupoId: string | number) {
  return `${GRUPO_EMPRESARIAL_API}/${grupoId}/contactos`;
}

function assertGrupoId(grupoId: unknown): asserts grupoId is string | number {
  if (grupoId == null || grupoId === '') {
    throw new Error('grupoId es requerido para contactos del grupo');
  }
}

const defaultListRequest = (grupoId: string | number) => ({
  grupoId,
  page: 0,
  size: 99999,
  sort: 'id,asc',
});

// Actions

export const getEntities = createAsyncThunk(
  'contacto/fetch_entity_list',
  async (request: any) => {
    const { grupoId, filters, ...rest } = request;
    assertGrupoId(grupoId);
    const params = getAllRequest({
      ...rest,
      filters: (filters ?? []).filter((f: any) => f?.filterName !== 'grupoId'),
    });
    const requestUrl = `${contactosCollectionUrl(grupoId)}?${params}&cacheBuster=${new Date().getTime()}`;
    return axios.get<any[]>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const getEntity = createAsyncThunk(
  'contacto/fetch_entity',
  async (arg: { grupoId: string | number; id: string | number }) => {
    const { grupoId, id } = arg;
    assertGrupoId(grupoId);
    const requestUrl = `${contactosCollectionUrl(grupoId)}/${id}`;
    return axios.get<any>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'contacto/create_entity',
  async (entity: any, thunkAPI) => {
    const { grupoId, ...bodyFields } = entity;
    assertGrupoId(grupoId);
    const result = await axios.post<any>(contactosCollectionUrl(grupoId), cleanEntity(bodyFields));
    thunkAPI.dispatch(getEntities(defaultListRequest(grupoId)));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'contacto/update_entity',
  async (entity: any, thunkAPI) => {
    const { grupoId, ...rest } = entity;
    assertGrupoId(grupoId);
    const id = rest.id;
    const result = await axios.put<any>(`${contactosCollectionUrl(grupoId)}/${id}`, cleanEntity(rest));
    thunkAPI.dispatch(getEntities(defaultListRequest(grupoId)));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const partialUpdateEntity = createAsyncThunk(
  'contacto/partial_update_entity',
  async (entity: any, thunkAPI) => {
    const { grupoId, ...rest } = entity;
    assertGrupoId(grupoId);
    const id = rest.id;
    const result = await axios.patch<any>(`${contactosCollectionUrl(grupoId)}/${id}`, cleanEntity(rest));
    thunkAPI.dispatch(getEntities(defaultListRequest(grupoId)));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'contacto/delete_entity',
  async (arg: { grupoId: string | number; id: string | number }, thunkAPI) => {
    const { grupoId, id } = arg;
    assertGrupoId(grupoId);
    const requestUrl = `${contactosCollectionUrl(grupoId)}/${id}`;
    const result = await axios.delete<any>(requestUrl);
    thunkAPI.dispatch(getEntities(defaultListRequest(grupoId)));
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const ContactoSlice = createEntitySlice({
  name: 'contacto',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
          totalItems: parseInt(headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = ContactoSlice.actions;

// Reducer
export default ContactoSlice.reducer;
